import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import AppRoutes from 'routes';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { ScriptsLoader } from 'components/ScriptsLoader/ScriptsLoader';
import { ToastContainer } from 'react-toastify';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { assignFeatureFlags } from 'hooks/useFeatureFlags';
import { UrlParams } from 'enums/UrlParams';
import { ClickToComponent } from 'click-to-react-component';

import store from './store';

import 'react-toastify/dist/ReactToastify.min.css';
import './index.scss';
import { injectStore } from './store/injectStore';

// Set injected store here, so we can get it elsewhere without causing circular dependency
// https://redux.js.org/faq/code-structure#how-can-i-use-the-redux-store-in-non-component-files
injectStore(store);

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new Integrations.BrowserTracing()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
  });
}

initializeAnalytics();
assignFeatureFlags();

const app = (
  <>
    <ClickToComponent />
    <ScriptsLoader>
      <Provider store={store}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <ToastContainer limit={1} />
      </Provider>
    </ScriptsLoader>
  </>
);

ReactDOM.render(app, document.getElementById('root'));

function initializeAnalytics() {
  window.analytics = AnalyticsBrowser.load({
    writeKey: process.env.REACT_APP_ANALYTICS_WRITE_KEY as string,
  });

  parseCrossDomainConversionTrackingParams();

  analytics.page();
}

function parseCrossDomainConversionTrackingParams() {
  const urlParams = new URLSearchParams(window.location.search);

  // This tracks users with the same ID between the marketing site and the product.
  const anonymousId = urlParams.get(UrlParams.AnonymousId);
  if (anonymousId) {
    analytics.setAnonymousId(anonymousId);
  }

  // This is the Facebook click identifier which is set if the user clicked a Facebook ad.
  // We pass this from the marketing site to the product.
  const fbc = urlParams.get(UrlParams.FBC);

  // This is the Facebook pixel identifier that is passed from the marketing site to the product.
  const fbp = urlParams.get(UrlParams.FBP);

  if (fbc || fbp) {
    analytics.identify({ ...(fbc && { fbc }), ...(fbp && { fbp }) });
  }
}
